import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Autocomplete,
  Snackbar,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Chip
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { api } from '../../utils/api';
import CompleteSaleDialog from './CompleteSaleDialog';

const Cart = () => {
  const [cartItems, setCartItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openCustomerDialog, setOpenCustomerDialog] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [newCustomer, setNewCustomer] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    notes: '',
    countryCode: '39'
  });
  const [customerFormErrors, setCustomerFormErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [notes, setNotes] = useState('');
  const [completeSaleDialogOpen, setCompleteSaleDialogOpen] = useState(false);
  const [createdSale, setCreatedSale] = useState(null);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');

  useEffect(() => {
    loadCartItems();
    fetchCustomers();
    fetchCurrentUser();
  }, []);

  const loadCartItems = () => {
    const items = JSON.parse(localStorage.getItem('cart') || '[]');
    // Sanitize cart items to ensure numeric values
    const sanitizedItems = items.map(item => ({
      ...item,
      price: parseFloat(item.price) || 0,
      quantity: parseInt(item.quantity) || 1,
      total: parseFloat(item.total) || (parseFloat(item.price) * parseInt(item.quantity)) || 0,
      // Preserva i campi per articoli personalizzati
      isCustom: item.isCustom || false,
      productId: item.productId,
      purchasePrice: parseFloat(item.purchasePrice) || 0
    }));
    setCartItems(sanitizedItems);
  };

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers');
      setCustomers(response.data);
    } catch (error) {
      console.error('Errore nel caricamento dei clienti:', error);
      setSnackbar({
        open: true,
        message: 'Errore nel caricamento dei clienti',
        severity: 'error'
      });
    }
  };

  const fetchCurrentUser = async () => {
    try {
      console.log('Tentativo di recupero utente corrente...');
      // Utilizzo l'endpoint /api/auth/me che funziona correttamente
      const response = await api.get('/auth/me');
      console.log('Utente corrente ricevuto:', response.data);
      setCurrentUser(response.data);
    } catch (error) {
      console.error('Errore nel caricamento dell\'utente corrente:', error);
      
      // Fallback: prova ad ottenere l'utente dal localStorage se disponibile
      try {
        const authData = JSON.parse(localStorage.getItem('authData'));
        if (authData && authData.user) {
          console.log('Utilizzando dati utente dal localStorage:', authData.user);
          setCurrentUser(authData.user);
        } else {
          console.error('Nessun dato utente nel localStorage');
          setSnackbar({
            open: true,
            message: 'Errore nel caricamento dell\'utente corrente. Riprova o effettua nuovamente il login.',
            severity: 'error'
          });
        }
      } catch (localStorageError) {
        console.error('Errore nel recupero dati dal localStorage:', localStorageError);
        setSnackbar({
          open: true,
          message: 'Errore nel caricamento dell\'utente corrente. Riprova o effettua nuovamente il login.',
          severity: 'error'
        });
      }
    }
  };

  const handleCustomerChange = (event, newValue) => {
    setSelectedCustomer(newValue);
  };

  const handleOpenCustomerDialog = () => {
    setOpenCustomerDialog(true);
  };

  const handleCloseCustomerDialog = () => {
    setOpenCustomerDialog(false);
    setNewCustomer({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      postalCode: '',
      notes: '',
      countryCode: '39'
    });
    setCustomerFormErrors({});
  };

  const validateCustomerForm = () => {
    const errors = {};
    if (!newCustomer.firstName.trim()) errors.firstName = 'Nome richiesto';
    if (!newCustomer.lastName.trim()) errors.lastName = 'Cognome richiesto';
    if (!newCustomer.phone.trim()) errors.phone = 'Telefono richiesto';
    // Gli altri campi sono opzionali secondo il modello
    if (newCustomer.email && !newCustomer.email.match(/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/)) 
      errors.email = 'Email non valida';
    return errors;
  };

  const handleAddCustomer = async () => {
    const errors = validateCustomerForm();
    if (Object.keys(errors).length > 0) {
      setCustomerFormErrors(errors);
      return;
    }

    try {
      const response = await api.post('/customers', newCustomer);
      setCustomers([...customers, response.data]);
      setSelectedCustomer(response.data);
      handleCloseCustomerDialog();
    } catch (error) {
      console.error('Errore nell\'aggiunta del cliente:', error);
      setSnackbar({
        open: true,
        message: 'Errore nell\'aggiunta del cliente',
        severity: 'error'
      });
    }
  };

  const handleCustomerInputChange = (field) => (event) => {
    setNewCustomer({
      ...newCustomer,
      [field]: event.target.value
    });
    // Rimuovi l'errore quando l'utente inizia a digitare
    if (customerFormErrors[field]) {
      setCustomerFormErrors({
        ...customerFormErrors,
        [field]: ''
      });
    }
  };

  const handleQuantityChange = (index, change) => {
    const updatedItems = [...cartItems];
    const item = updatedItems[index];
    
    // Se l'articolo è personalizzato o non ha un riferimento a un prodotto reale
    // dobbiamo solo aggiornare il suo totale senza controlli di magazzino
    if (item.isCustom || !item.productId || item.productId.toString().startsWith('custom-')) {
      const newQuantity = Math.max(1, (parseInt(item.quantity) || 1) + change);
      item.quantity = newQuantity;
      item.total = item.price * newQuantity;
      setCartItems(updatedItems);
      localStorage.setItem('cart', JSON.stringify(updatedItems));
      return;
    }

    // Per i prodotti normali, continuiamo con la logica esistente
    // ... existing code per i prodotti normali ...
    const newQuantity = Math.max(1, (parseInt(item.quantity) || 1) + change);
    
    // Verifica se è disponibile la quantità richiesta (solo per prodotti del catalogo)
    api.get(`/products/${item.productId}`)
      .then(response => {
        const product = response.data;
        if (newQuantity <= product.stock) {
          item.quantity = newQuantity;
          item.total = item.price * newQuantity;
          setCartItems(updatedItems);
          localStorage.setItem('cart', JSON.stringify(updatedItems));
        } else {
          setSnackbar({
            open: true,
            message: 'Quantità non disponibile in magazzino',
            severity: 'error'
          });
        }
      })
      .catch(error => {
        console.error('Errore nel controllo disponibilità:', error);
        // In caso di errore nella verifica, aggiorna comunque la quantità
        item.quantity = newQuantity;
        item.total = item.price * newQuantity;
        setCartItems(updatedItems);
        localStorage.setItem('cart', JSON.stringify(updatedItems));
      });
  };

  const handleRemoveItem = (index) => {
    const updatedItems = cartItems.filter((_, i) => i !== index);
    setCartItems(updatedItems);
    localStorage.setItem('cart', JSON.stringify(updatedItems));
  };

  const handleCheckout = () => {
    if (!selectedCustomer) {
      setSnackbar({
        open: true,
        message: 'Seleziona un cliente per procedere',
        severity: 'warning'
      });
      return;
    }

    if (cartItems.length === 0) {
      setSnackbar({
        open: true,
        message: 'Il carrello è vuoto',
        severity: 'warning'
      });
      return;
    }

    // Prepara i dati della vendita da passare al dialog
    const saleData = {
      customerId: selectedCustomer.id,
      customer: selectedCustomer,
      items: cartItems.map(item => {
        // Prepara l'oggetto di base dell'item
        const isCustomItem =
          item.isCustom ||
          (item.productId && item.productId.toString().startsWith('ART-PER-')) ||
          (!item.productId && (parseFloat(item.purchasePrice) || 0) > 0);

        const itemData = {
          productId: item.productId,
          code: item.code,
          quantity: item.quantity,
          price: item.price,
          description: item.description,
          includeLabor: item.includeLabor,
          total: item.total,
          isCustom: isCustomItem,
          purchasePrice: isCustomItem ? parseFloat(item.purchasePrice) || 0 : undefined,
          Product: {
            id: item.productId,
            name: item.description,
            price: item.price,
            stock: item.stock || 0
          }
        };
        return itemData;
      }),
      notes: notes,
      total: calculateTotal()
    };
    setCreatedSale(saleData);
    setCompleteSaleDialogOpen(true);
  };

  const handleSaleCompleted = () => {
    // Svuota il carrello
    localStorage.removeItem('cart');
    setCartItems([]);
    setSelectedCustomer(null);
    setNotes('');
    setCreatedSale(null);
    
    setSnackbar({
      open: true,
      message: 'Vendita completata con successo',
      severity: 'success'
    });
  };

  const calculateTotal = () => {
    return cartItems.reduce((total, item) => {
      const price = parseFloat(item.price) || 0;
      const quantity = parseInt(item.quantity) || 1;
      return total + (price * quantity);
    }, 0);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={2}>
        {/* Cliente */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <FormControl sx={{ flexGrow: 1 }}>
                <Autocomplete
                  options={customers}
                  getOptionLabel={(option) =>
                    option && option.firstName && option.lastName
                      ? `${option.firstName} ${option.lastName}${option.phone ? ' (' + option.phone + ')' : ''}`
                      : ''
                  }
                  value={selectedCustomer}
                  inputValue={customerSearchTerm}
                  onInputChange={async (event, newInputValue) => {
                    setCustomerSearchTerm(newInputValue);
                    if (newInputValue.length >= 2) {
                      try {
                        const response = await api.get(`/customers/search/${encodeURIComponent(newInputValue)}`);
                        setCustomers(response.data);
                      } catch (error) {
                        setCustomers([]);
                      }
                    } else {
                      setCustomers([]);
                    }
                  }}
                  onChange={handleCustomerChange}
                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Cliente"
                      required
                      fullWidth
                      variant="outlined"
                    />
                  )}
                />
              </FormControl>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleOpenCustomerDialog}
              >
                Nuovo Cliente
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Carrello */}
        <Grid item xs={12}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Prodotto</TableCell>
                  <TableCell align="right">Prezzo unitario</TableCell>
                  <TableCell align="center">Quantità</TableCell>
                  <TableCell align="right">Totale</TableCell>
                  <TableCell align="center">Azioni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cartItems.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body1">
                          {item.description}
                          {item.isCustom && (
                            <Chip 
                              label="Personalizzato" 
                              size="small" 
                              color="primary" 
                              sx={{ ml: 1 }} 
                            />
                          )}
                          {item.includeLabor && (
                            <Chip 
                              label="Con manodopera" 
                              size="small" 
                              color="secondary" 
                              sx={{ ml: 1 }} 
                            />
                          )}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">€{parseFloat(item.price || 0).toFixed(2)}</TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <IconButton 
                          size="small" 
                          onClick={() => handleQuantityChange(index, -1)}
                        >
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                        <Typography sx={{ mx: 1 }}>{item.quantity}</Typography>
                        <IconButton 
                          size="small" 
                          onClick={() => handleQuantityChange(index, 1)}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell align="right">€{parseFloat(item.total || 0).toFixed(2)}</TableCell>
                    <TableCell align="center">
                      <IconButton 
                        color="error" 
                        onClick={() => handleRemoveItem(index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {cartItems.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography variant="body1" color="text.secondary">
                        Il carrello è vuoto
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        {/* Totale e Checkout */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                Totale: €{calculateTotal().toFixed(2)}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleCheckout}
                disabled={loading || cartItems.length === 0 || !selectedCustomer}
              >
                {loading ? <CircularProgress size={24} /> : 'Completa Vendita'}
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Customer Dialog */}
      <Dialog open={openCustomerDialog} onClose={handleCloseCustomerDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Nuovo Cliente</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Cognome"
                value={newCustomer.lastName}
                onChange={handleCustomerInputChange('lastName')}
                error={!!customerFormErrors.lastName}
                helperText={customerFormErrors.lastName}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nome"
                value={newCustomer.firstName}
                onChange={handleCustomerInputChange('firstName')}
                error={!!customerFormErrors.firstName}
                helperText={customerFormErrors.firstName}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                value={newCustomer.email}
                onChange={handleCustomerInputChange('email')}
                error={!!customerFormErrors.email}
                helperText={customerFormErrors.email}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  sx={{ width: '80px' }}
                  label="Prefisso"
                  value={newCustomer.countryCode}
                  onChange={handleCustomerInputChange('countryCode')}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">+</InputAdornment>,
                  }}
                />
                <TextField
                  fullWidth
                  label="Telefono"
                  value={newCustomer.phone}
                  onChange={handleCustomerInputChange('phone')}
                  error={!!customerFormErrors.phone}
                  helperText={customerFormErrors.phone}
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Indirizzo"
                value={newCustomer.address}
                onChange={handleCustomerInputChange('address')}
                error={!!customerFormErrors.address}
                helperText={customerFormErrors.address}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Città"
                value={newCustomer.city}
                onChange={handleCustomerInputChange('city')}
                error={!!customerFormErrors.city}
                helperText={customerFormErrors.city}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="CAP"
                value={newCustomer.postalCode}
                onChange={handleCustomerInputChange('postalCode')}
                error={!!customerFormErrors.postalCode}
                helperText={customerFormErrors.postalCode}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Note"
                value={newCustomer.notes}
                onChange={handleCustomerInputChange('notes')}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCustomerDialog}>Annulla</Button>
          <Button onClick={handleAddCustomer} variant="contained">
            Aggiungi Cliente
          </Button>
        </DialogActions>
      </Dialog>

      {/* CompleteSaleDialog */}
      <CompleteSaleDialog
        open={completeSaleDialogOpen}
        onClose={() => setCompleteSaleDialogOpen(false)}
        sale={createdSale}
        onCompleted={handleSaleCompleted}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Cart; 